import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import { formatDateFromApi } from '../../utils/dateTime';
import {
  getNotificationChannelLabel,
  getNotificationChannelSettingList,
  NOTIFICATION_CHANNEL_OPTIONS,
  type NotificationChannelSetting,
} from '../../utils/notificationChannelSetting';
import { Bell, Edit, Plus } from 'lucide-react';

const CHANNEL_FILTER_OPTIONS = [
  { value: 'all', label: '전체 채널' },
  ...NOTIFICATION_CHANNEL_OPTIONS,
];

const NotificationChannelList: React.FC = () => {
  const navigate = useNavigate();
  const { projectCode: projectCodeFromUrl } = useParams<{ projectCode: string }>();

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('notification-channel-list');

  const [selectedChannel, setSelectedChannel] = useState('all');
  const [channels, setChannels] = useState<NotificationChannelSetting[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    if (!projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (!projectInfo) {
      navigate('/projects', { replace: true });
      return;
    }
    const detail: ProjectDetail = {
      id: (projectInfo as { id?: string }).id ?? projectInfo.code ?? '',
      ...projectInfo,
    };
    if (detail.code !== projectCodeFromUrl) {
      navigate('/projects', { replace: true });
      return;
    }
    setProjectDetail(detail);
  }, [projectCodeFromUrl, navigate]);

  useEffect(() => {
    if (!projectCodeFromUrl || !projectDetail) return;
    let cancelled = false;
    setError(null);
    setIsLoading(true);
    getNotificationChannelSettingList(projectCodeFromUrl)
      .then((res) => {
        if (!cancelled) setChannels(res.notificationChannelSettings ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '알림 채널 목록을 불러오지 못했습니다.');
          setChannels([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectCodeFromUrl, projectDetail]);

  const filteredChannels = channels.filter(
    (item) => selectedChannel === 'all' || item.channel === selectedChannel
  );

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return formatDateFromApi(dateString) || dateString;
  };

  if (!projectDetail) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-gray-500">프로젝트 정보를 불러올 수 없습니다.</p>
          <button
            type="button"
            onClick={() => navigate('/projects')}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            프로젝트 목록으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="알림 채널"
          subtitle={
            projectCodeFromUrl
              ? `${projectDetail.name} (${projectCodeFromUrl})`
              : '알림 채널 목록을 관리합니다'
          }
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-full">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                {error}
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="ml-2 text-red-800 underline"
                >
                  닫기
                </button>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full">
                  <select
                    value={selectedChannel}
                    onChange={(e) => setSelectedChannel(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    {CHANNEL_FILTER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    type="button"
                    onClick={() =>
                      projectCodeFromUrl &&
                      navigate(`/projects/${projectCodeFromUrl}/notification-channel-settings/create`)
                    }
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    알림 채널 추가
                  </button>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  총 <span className="font-medium text-gray-900">{channels.length}</span>개 채널 /
                  <span className="ml-1">
                    검색결과 <span className="font-medium text-gray-900">{filteredChannels.length}</span>개
                  </span>
                </p>
                <div className="flex items-center gap-4 text-sm">
                  {NOTIFICATION_CHANNEL_OPTIONS.map((option) => (
                    <span key={option.value} className="flex items-center">
                      {option.label}: {channels.filter((item) => item.channel === option.value).length}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="pl-6 pr-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        채널
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        발송키
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        생성일
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">액션</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {isLoading ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center">
                          <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                          </div>
                        </td>
                      </tr>
                    ) : filteredChannels.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center">
                          <Bell className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-500">등록된 알림 채널이 없습니다.</p>
                        </td>
                      </tr>
                    ) : (
                      filteredChannels.map((item) => (
                        <tr key={item.channel} className="hover:bg-gray-50">
                          <td className="pl-6 pr-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {getNotificationChannelLabel(item.channel)}
                            <span className="ml-2 text-xs font-normal text-gray-500">{item.channel}</span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600 font-mono break-all">
                            {item.senderKey || '—'}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(item.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              type="button"
                              className="text-gray-400 hover:text-blue-600"
                              title="수정"
                              onClick={() =>
                                projectCodeFromUrl &&
                                navigate(
                                  `/projects/${projectCodeFromUrl}/notification-channel-settings/${encodeURIComponent(item.channel)}/update`
                                )
                              }
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default NotificationChannelList;
